import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    Input,
    QueryList,
    ViewChild,
    inject
} from "@angular/core";
import { Observable, combineLatest, merge, of } from "rxjs";
import { map } from "rxjs/operators";
import { IconComponent } from "~app/common/components/icon/icon.component";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { EntityType, RuleErrorContext, RuleSeverityLevel, RuleValidationMessage } from "~app/common/services/cqs/models/rule-validation-message";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";

interface ValidationIconViewModel{
    icon: string;
    messages: RuleValidationMessage[];
}

@Component({
    selector: "rask-virkeskop-validation-icon-popover",
    templateUrl: "./virkeskop-validation-icon-popover.component.html",
    styleUrls: ["./virkeskop-validation-icon-popover.component.scss"],
    imports: [RaskCommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirkeskopValidationIconPopoverComponent implements AfterContentInit {
    @ContentChildren("message") content: QueryList<ElementRef>;
    @Input() context?: RuleErrorContext;
    @Input() entityGuid?: string;
    @Input() entityType?: EntityType;
    @ViewChild("icon") iconRef: IconComponent;
    @Input() iconStyle = "fal";
    @Input() messageId: string[];

    protected messages$: Observable<ValidationIconViewModel>;

    private readonly validationService = inject(VirkeskopValidationService);

    closePopover() {
        this.iconRef?.popoverRef.close();
    }

    ngAfterContentInit(): void {
        // Kör init i "AfterContentInit" då contentChildren inte existerar innan det
        // mergar in en of(null) för att emitta vid init

        const contentMessages$ = merge(of(null), this.content.changes).pipe(
            map(() => this.content.map(ref => this.createErrorMessage(ref.nativeElement.innerText)))
        );

        const contextMessages$ = this.validationService.filterVisibleMessages(this.messageId, this.entityType, this.entityGuid, this.context);

        this.messages$ = combineLatest([
            contentMessages$,
            contextMessages$
        ]).pipe(
            map(([
                contentMessages,
                contextMessages
            ]) => [
                ...contentMessages,
                ...contextMessages
            ]),
            map(messages => ({
                messages,
                icon: `${this.iconStyle} ${this.getMostSevereIcon(messages)}`
            }))
        );
    }

    openPopover() {
        this.iconRef?.popoverRef.open();
    }

    private createErrorMessage(text: string): RuleValidationMessage {
        const message = new RuleValidationMessage();
        message.message = text;
        message.severity = RuleSeverityLevel.error;
        return message;
    }

    private getMostSevereIcon(messages: RuleValidationMessage[]) {
        if(messages.length === 0) {
            return "";
        }

        let severity = RuleSeverityLevel.info;

        for(const message of messages) {
            if(message.severity > severity) {
                severity = message.severity;
            }
        }

        switch (severity) {
            case RuleSeverityLevel.error:
                return "far fa-circle-exclamation error";
            case RuleSeverityLevel.warning:
                return "far fa-square-exclamation warning";
            default:
                return "far fa-info-circle";
        }
    }
}
